import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material'
import { GridLogicOperator } from '@mui/x-data-grid-premium'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { useState, RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { getPromptColumn } from '../helpers/getPromptColumn'
import { useCreateSmartFilterMutation } from '../helpers/useCreateSmartFilterMutation'

interface AiFilterDialogProps {
  open: boolean
  onClose: () => void
  tableName: string
  apiRef: RefObject<GridApiPremium>
}

const ErrorBanner = styled(Box)(({ theme }) => ({
  border: theme.borders.stroke.default,
  borderColor: theme.palette.border.danger.strong,
  color: theme.palette.text.danger.onDangerMuted,
  fontWeight: 400,
  background: theme.palette.bg.danger.muted,
  padding: '8px 16px',
  marginBottom: '16px',
  borderRadius: '8px',
}))

export const AiFilterDialog: React.FC<AiFilterDialogProps> = ({ apiRef, open, onClose, tableName }) => {
  const { t } = useTranslation('requests')
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const { mutateAsync } = useCreateSmartFilterMutation()

  async function generateFilterModel(prompt: string): Promise<boolean> {
    if (!apiRef.current) return false
    const columns = apiRef.current.getAllColumns()
    const promptColumns = columns.map(getPromptColumn)

    const items = await mutateAsync(
      {
        input: prompt,
        columns: promptColumns,
        tableType: tableName.toLowerCase(),
      },
      {
        onError: () => {
          setError(true)
          setLoading(false)
        },
      }
    )

    if (!Array.isArray(items) || items.length === 0) {
      return false
    }

    track('AIFilterUsed', {
      tableName: tableName.toLowerCase(),
      prompt,
      response: JSON.stringify(items),
    })

    // Currently all generated filters are AND filters
    apiRef.current.setFilterModel({ items, logicOperator: GridLogicOperator.And })
    return true
  }

  const closeDialog = () => {
    onClose()
    setLoading(false)
    setPrompt('')
    setError(false)
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError(false)

    const isSuccessful = await generateFilterModel(prompt)

    setLoading(false)

    if (isSuccessful) {
      closeDialog()
    } else {
      setError(true)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClick={(e) => e.stopPropagation()}
    >
      {loading ? (
        <Stack
          direction="row"
          gap={4}
          sx={{ position: 'absolute', height: '100%', width: '100%', zIndex: 9 }}
          justifyContent="center"
          alignItems="center"
        >
          <Typography variant="upsized-medium">{t('ai-filter.loading-text')}</Typography>
          <CircularProgress size={30} />
        </Stack>
      ) : (
        <>
          <DialogTitle>{t('ai-filter.title')}</DialogTitle>
          <DialogContent sx={{ marginTop: 4 }}>
            {error && <ErrorBanner>{t('ai-filter.error-text')}</ErrorBanner>}
            <TextField
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('ai-filter.placeholder')}
              sx={{ width: '100%' }}
            />
          </DialogContent>
        </>
      )}
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={closeDialog}>
          {t('ai-filter.close')}
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {t('ai-filter.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
